import { useMemo } from "react";
import { match, P } from "ts-pattern";
import { useSummary } from "@/hooks/useSummary";
import { Fluctuation, OTHER_INQUIRY_TITLE, OTHER_PROBLEM_TITLE, Problem } from "@/models/summary";
import { useCategorySummaryStore } from "./useCategorySummaryStore";

/**
 * メインと比較パネルの課題を仕様通りにソートして取得する関数
 *
 * 単独分析時の課題の表示仕様:
 * 1. 課題は件数の多い順に表示する
 * 2. その他系は件数に関わらず下に表示する
 * 3. 0件の課題は隠す
 *
 * 比較分析時の課題の表示仕様:
 * 1. 課題はメインも分析側も、メインの件数の多い順、比較の件数の多い順、その他系の順番で表示する (=メインと比較に表示される課題および並び順が同じになる)
 * 2. メインまたは比較パネルのどちらかが0件の場合、0件側に「対象の課題に分類されたデータはありません」という文字列を表示する
 * 3. メインまたは比較パネルの両方で0件の課題は表示しない
 * 4. メインと比較パネルで同じカテゴリが選択されている場合、同じ課題の件数に増減があればでup/downの矢印を表示し、テキストの色も変える
 */
const getSortedProblems = (mainProblems: Problem[], compProblems: Problem[]) => {
  // 最初にAとBの課題タイトルのリストを作る
  const excludeOthers = (p: Problem) => ![OTHER_INQUIRY_TITLE, OTHER_PROBLEM_TITLE].includes(p.title);
  const uniqueTitles = Array.from(
    new Set(
      [...mainProblems.filter(excludeOthers), ...compProblems.filter(excludeOthers)]
        .map((problem) => problem.title)
        .concat([OTHER_PROBLEM_TITLE, OTHER_INQUIRY_TITLE]),
    ),
  );

  // 課題タイトルをmapして、[[Aの課題1, Bの課題1], [Aの課題2, Bの課題2], ...] の形にする
  // 該当タイトルの課題がAまたはBに存在しない場合は空のProblemを作成(空のProblemだった場合はUI側で「対象の課題に分類されたデータはありません」と表示)
  const pairs = uniqueTitles.map(title => {
    const mainProblem = mainProblems.find(p => p.title === title);
    const compProblem = compProblems.find(p => p.title === title);
    const mainCount = mainProblem ? mainProblem.caseCount : 0;
    const compCount = compProblem ? compProblem.caseCount : 0;
    const emptyProblem = Problem.createEmptyProblem(title);
    const fluctuation = match<number, Fluctuation>(mainCount)
      .with(P.when((c) => c > compCount), () => "increased")
      .with(P.when((c) => c < compCount), () => "decreased")
      .otherwise(() => "unchanged");

    return mainCount > 0 || compCount > 0
      ? [
        mainProblem ? mainProblem.updateFluctuation(fluctuation) : emptyProblem,
        compProblem ?? emptyProblem, // 比較パネルでは増減は表示しない
      ]
      : [];
  }).filter((pair) => pair.length > 0); // A,B両方0の場合は除外する

  return {
    mainProblems: pairs.map(pair => pair[0]),
    compProblems: pairs.map(pair => pair[1]),
  };
};

export const useComparisonMode = () => {
  const {
    mainSelectedAnalysisId,
    mainSelectedCategoryPath,
    compSelectedAnalysisId,
    compSelectedCategoryPath,
  } = useCategorySummaryStore();

  // 現在のパネルのsummaryを取得
  const { summary: mainSummary } = useSummary(mainSelectedAnalysisId, mainSelectedCategoryPath);
  const _mainProblems = useMemo(() => mainSummary?.problems ?? [], [mainSummary]);

  // 比較対象パネルのsummaryを取得
  const { summary: compSummary } = useSummary(compSelectedAnalysisId, compSelectedCategoryPath);
  const _compProblems = useMemo(() => compSummary?.problems ?? [], [compSummary]);

  // メインと比較の課題をソートして取得
  const { mainProblems, compProblems } = getSortedProblems(_mainProblems, _compProblems);

  return {
    mainProblems,
    compProblems,
  };
};
